import { getFields } from 'types';
import { EditJobsiteConfigurationFormData } from 'containers/jobsiteConfiguration/types';
import {
  SelfOnboardingState,
  SelfOnboardingStepKey,
} from 'containers/selfOnboarding/types';
import {
  SequentialConfigStep,
  SequentialStepArgs,
  WhiteListedStep,
} from './useSelfOnboardingNavigation.config';

/**
 * Navigation config for the *authenticated* self-onboarding flow (the forked,
 * isolated new flow). Unlike the legacy config it takes no feature-flag
 * parameter — the route entry selects this flow once, so everything here is
 * unconditionally the new-flow shape.
 *
 * Flow order (see the sc-16468 plan): language → phone → OTP → account creation
 * (name only, Mutation A) → contractor selection (Mutation B) → jobsite data
 * (DOB / SSN, plus email — collected here, not at account creation) → profile →
 * documents → photo → review. Phone is verified up front, so there is no
 * end-of-flow mobile-verification step.
 */

/** Brand-new workers set their name + create their account here. Existing workers (signed in at OTP) already have a worker and skip it. */
const accountCreationStep: SequentialConfigStep = {
  key: SelfOnboardingStepKey.WorkerName,
  isVisible: (args): boolean => !args?.worker,
};

/**
 * Always visited: this is where the jobsiteWorker is bound to the jobsite
 * (Mutation B), for both new and existing workers, regardless of whether a
 * contractor picker is shown.
 */
const contractorSelectionStep: SequentialConfigStep =
  SelfOnboardingStepKey.ContractorSelection;

/**
 * Jobsite-specific data (DOB, SSN last four, config-gated fields), collected
 * after the worker is bound to the jobsite. Reuses the BasicInfo step key; the
 * authenticated flow maps it to its own component.
 */
const jobsiteDataAndDocumentSteps: SequentialConfigStep[] = [
  SelfOnboardingStepKey.BasicInfo,
  SelfOnboardingStepKey.Profile,
  {
    key: SelfOnboardingStepKey.EmergencyContactInfo,
    isVisible: (args): boolean => {
      const jobsiteConfigWorkerInfo =
        getFields<EditJobsiteConfigurationFormData['workerInfo']>();
      const stepConfig = args.getStepConfig();
      return !stepConfig.fields?.[jobsiteConfigWorkerInfo.emergencyContact]
        ?.isHidden;
    },
  },
  SelfOnboardingStepKey.GovernmentIssuedId,
  SelfOnboardingStepKey.NycSiteSafetyTrainingCard,
  SelfOnboardingStepKey.OshaCard,
  SelfOnboardingStepKey.TwicCard,
  SelfOnboardingStepKey.JobsiteSafetyVideo,
  SelfOnboardingStepKey.JobsiteSafetyDocument,
  SelfOnboardingStepKey.LIRRBlueTraining,
  SelfOnboardingStepKey.MustSafetyReport,
  SelfOnboardingStepKey.ConfinedSpacesTraining,
  SelfOnboardingStepKey.ProfilePhoto,
];

export const authenticatedSequentialSteps: SequentialConfigStep[] = [
  SelfOnboardingStepKey.LanguageSelection,
  {
    key: SelfOnboardingStepKey.Login,
    isVisible: (args): boolean => !args?.worker,
  },
  accountCreationStep,
  contractorSelectionStep,
  ...jobsiteDataAndDocumentSteps,
  SelfOnboardingStepKey.ReviewCompletedProfile,
];

const lastSequentialStep =
  authenticatedSequentialSteps[authenticatedSequentialSteps.length - 1];
export const authenticatedLastStep =
  typeof lastSequentialStep === 'string'
    ? lastSequentialStep
    : lastSequentialStep.key;

export const authenticatedNoOrderInfoSteps: SelfOnboardingStepKey[] = [
  SelfOnboardingStepKey.LanguageSelection,
  SelfOnboardingStepKey.Login,
  SelfOnboardingStepKey.ConfirmLoginCode,
  SelfOnboardingStepKey.WorkerName,
  SelfOnboardingStepKey.ContractorSelection,
  SelfOnboardingStepKey.ReviewCompletedProfile,
];

// The first interactive step is always Login (phone). Contractor selection is
// reached after account creation, so it is never the entry step.
export const authenticatedNoGoBackSteps: SelfOnboardingStepKey[] = [
  SelfOnboardingStepKey.Login,
  SelfOnboardingStepKey.Closed,
];

type WhiteListedStepState = Pick<
  SelfOnboardingState,
  'didWorkerMatch' | 'userAccountId' | 'phoneVerificationToken'
>;

const isPhoneVerifiedOrSignedIn = (state: WhiteListedStepState): boolean =>
  Boolean(state?.phoneVerificationToken || state?.userAccountId);

const isSignedIn = (state: WhiteListedStepState): boolean =>
  Boolean(state?.userAccountId);

const isWorkerMatchedOrSignedIn = (state: WhiteListedStepState): boolean =>
  Boolean(state?.didWorkerMatch || state?.userAccountId);

/**
 * Only the pre-account steps (language + phone + OTP) are freely reachable by an
 * unauthenticated visitor. Account creation requires a verified phone; the
 * jobsite-bound steps require a session. Everything past account creation is
 * naturally gated by the presence of a session worker (the nav guard only
 * bounces when `worker === null`).
 */
export const authenticatedWhiteListedSteps: WhiteListedStep[] = [
  SelfOnboardingStepKey.LanguageSelection,
  SelfOnboardingStepKey.Login,
  SelfOnboardingStepKey.ConfirmLoginCode,
  {
    key: SelfOnboardingStepKey.WorkerName,
    isWhiteListed: isPhoneVerifiedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.ContractorSelection,
    isWhiteListed: isSignedIn,
  },
  {
    key: SelfOnboardingStepKey.BasicInfo,
    isWhiteListed: isSignedIn,
  },
  {
    key: SelfOnboardingStepKey.AlreadyCompleted,
    isWhiteListed: isWorkerMatchedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.Closed,
    isWhiteListed: isWorkerMatchedOrSignedIn,
  },
  {
    key: SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor,
    isWhiteListed: isSignedIn,
  },
  SelfOnboardingStepKey.UnableToVerifyAccount,
];

export const authenticatedPreviousSteps: Partial<
  Record<SelfOnboardingStepKey, SelfOnboardingStepKey>
> = {
  [SelfOnboardingStepKey.ConfirmLoginCode]: SelfOnboardingStepKey.Login,
};

/** Convenience: the fields the visible-steps builder needs from state. */
export type AuthenticatedSequentialStepArgs = Omit<
  SequentialStepArgs,
  'authenticatedSelfOnboardingEnabled'
>;
